// ════════════════════════════════════════════════════════════════════════════
//  ManDana — database membership
//  ---------------------------------------------------------------------------
//  A10 makes each membership edge its own authority document:
//
//    md:edge:<edgeId>          { id, dbId, k, v, at, order, by }
//    md:memberships:<dbId>     [edgeId, ...]      (projection only)
//
//  The edge owns this database's cells about one Record. The Record still owns
//  only its writing. Independent edge documents give sync one revision per
//  semantic target, so editing one member cannot stale an unrelated member.
//
//  The memberships document remains as a rebuildable ordering projection and a
//  migration bridge. First-generation bare ref keys and second-generation
//  {id,k,v,at} entries are upgraded in place to edge documents. A projection
//  can be rebuilt by scanning edges because every edge carries dbId and order.
// ════════════════════════════════════════════════════════════════════════════

interface MdMembershipEntry {
  id: string;
  k: string;
  v: Record<string, unknown>;
  at: number;
  /** Who wrote this edge last. An edge is the database's own document about one
      record, which makes it the honest home for the answer — the record itself
      is untouched by anything written here, so it has nothing to say about it.
      See md-organizer-door.ts for why every write carries this. Distinct from
      the per-property `o` in `m` below, which md-organizer.ts writes: `o` is the
      provenance of one cell VALUE and drives the review lifecycle, while this is
      the coarser fact of who last touched the edge itself. */
  by: 'user' | 'agent';
}

/* D3, per property rather than per edge. The organizer's state machine lives
   here and only here: `v[propertyId]` stays the raw value existing native
   views consume, and `m[propertyId]` carries the [origin, review, protection,
   provenance] tuple. Sanitised keys: o, r, p, prov. Edges written before this
   map existed have no `m` at all — they remain valid, and the first guarded
   organizer write adds it without losing any value. */
interface MdMembershipCellProvenance {
  model?: string;
  prompt?: string;
  refs?: string[];
  conf?: number;
  pid?: string;
  run?: string;
  at?: number;
}

interface MdMembershipCellMeta {
  o: string;                       // origin: 'ai' | 'user' | 'migration:<id>'
  r: string;                       // review: 'proposed' | 'accepted' | 'rejected'
  p: string;                       // protection: 'mutable' | 'user-locked'
  prov?: MdMembershipCellProvenance;
}

interface MdMembershipEdge extends MdMembershipEntry {
  dbId: string;
  order: number;
  m?: Record<string, MdMembershipCellMeta>;
}

(function (g: any) {
  'use strict';

  const P_PREFIX = 'md:memberships:';
  const E_PREFIX = 'md:edge:';
  const ID_RE = /^[a-z0-9]{6,40}$/;
  const MAX_MEMBERS = 5000;

  let seq = 0;
  function newId(): string {
    return 'm' + Date.now().toString(36) + (seq++).toString(36)
      + Math.random().toString(36).slice(2, 7);
  }

  const validDbId = (value: unknown): value is string =>
    typeof value === 'string' && ID_RE.test(value);
  const validEdgeId = (value: unknown): value is string =>
    typeof value === 'string' && ID_RE.test(value);
  const validRef = (value: unknown): value is string =>
    typeof value === 'string' && !!g.mdRecordRefParse(value);
  const projectionKey = (dbId: string): string => P_PREFIX + dbId;
  const edgeKey = (edgeId: string): string => E_PREFIX + edgeId;

  function entryOf(edge: MdMembershipEdge): MdMembershipEntry {
    return { id: edge.id, k: edge.k, v: edge.v, at: edge.at, by: edge.by };
  }

  /** Who is writing, asked of md-organizer-door.ts by late binding: this module loads
      before it, and a build without it behaves exactly as it did before any of
      this existed. Only the one word 'agent' counts as the agent — everything
      else, including nothing at all, is a person. */
  function actorNow(): 'user' | 'agent' {
    return (typeof g.mdOrganizerActor === 'function' && g.mdOrganizerActor() === 'agent')
      ? 'agent' : 'user';
  }

  /** An edge written before any of this existed was written by the person
      holding the device, because nothing else could write one. `user` is
      therefore the truth about those documents, not a shrug — which is why
      there is no third value for "unknown" and an unrecognised word reads as
      the person rather than as the machine. */
  const actorOf = (value: unknown): 'user' | 'agent' => value === 'agent' ? 'agent' : 'user';

  function sanitiseLegacy(value: unknown): { entries: MdMembershipEntry[]; repaired: boolean } {
    if (!Array.isArray(value)) return { entries: [], repaired: value != null };
    const seen = new Set<string>();
    const entries: MdMembershipEntry[] = [];
    let repaired = false;
    for (const item of value) {
      let entry: MdMembershipEntry | null = null;
      if (validRef(item)) {
        entry = { id: newId(), k: item, v: {}, at: Date.now(), by: 'user' };
        repaired = true;
      } else if (item && typeof item === 'object' && !Array.isArray(item)) {
        const raw = item as Partial<MdMembershipEntry>;
        if (validRef(raw.k)) {
          entry = {
            id: validEdgeId(raw.id) ? raw.id : newId(),
            k: raw.k,
            v: raw.v && typeof raw.v === 'object' && !Array.isArray(raw.v)
              ? raw.v as Record<string, unknown> : {},
            at: Number.isFinite(raw.at) ? raw.at as number : Date.now(),
            by: actorOf(raw.by),
          };
          if (entry.id !== raw.id || entry.v !== raw.v || entry.at !== raw.at) repaired = true;
        } else repaired = true;
      } else repaired = true;
      if (!entry || seen.has(entry.k)) { if (entry) repaired = true; continue; }
      seen.add(entry.k);
      entries.push(entry);
      if (entries.length >= MAX_MEMBERS) break;
    }
    return { entries, repaired };
  }

  const validOrigin = (value: unknown): value is string =>
    typeof value === 'string'
    && (value === 'ai' || value === 'user' || /^migration:[\w:.-]{1,60}$/.test(value));
  const validReview = (value: unknown): value is string =>
    value === 'proposed' || value === 'accepted' || value === 'rejected';
  const validProtection = (value: unknown): value is string =>
    value === 'mutable' || value === 'user-locked';
  const validPropertyId = (value: unknown): value is string =>
    typeof value === 'string' && /^[\w:/#.-]{1,200}$/.test(value);

  function safeRef(value: unknown): string | null {
    if (typeof value !== 'string' || value.length > 300) return null;
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password) return null;
      url.hash = '';
      return url.toString();
    } catch (e) { return null; }
  }

  /* Known keys only, each range-checked. Provenance pulled from another device
     is rendered next to the user's own writing, so anything unexpected is
     dropped field-by-field rather than trusted. */
  function sanitiseProvenance(value: unknown): MdMembershipCellProvenance | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    const raw = value as Record<string, unknown>;
    const out: MdMembershipCellProvenance = {};
    if (typeof raw.model === 'string' && raw.model.length <= 120) out.model = raw.model;
    if (typeof raw.prompt === 'string' && raw.prompt.length <= 120) out.prompt = raw.prompt;
    if (Array.isArray(raw.refs)) {
      out.refs = [...new Set(raw.refs.map(safeRef).filter(Boolean))].slice(0, 20) as string[];
    }
    if (Number.isFinite(raw.conf) && (raw.conf as number) >= 0 && (raw.conf as number) <= 1) {
      out.conf = raw.conf as number;
    }
    if (typeof raw.pid === 'string' && validEdgeId(raw.pid)) out.pid = raw.pid;
    if (typeof raw.run === 'string' && raw.run.length <= 120) out.run = raw.run;
    if (Number.isFinite(raw.at)) out.at = raw.at as number;
    return out;
  }

  /* A D3 entry whose enum fields cannot be validated cannot be trusted to make
     protection decisions, so it is dropped entry-by-entry. The cell VALUE in v
     is kept regardless — losing the tuple is a repair, losing the writing is
     data loss. */
  function sanitiseMetaMap(value: unknown): Record<string, MdMembershipCellMeta> | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    const out: Record<string, MdMembershipCellMeta> = {};
    for (const [propertyId, meta] of Object.entries(value as Record<string, unknown>)) {
      if (!validPropertyId(propertyId) || !meta || typeof meta !== 'object' || Array.isArray(meta)) continue;
      const raw = meta as Record<string, unknown>;
      if (!validOrigin(raw.o) || !validReview(raw.r) || !validProtection(raw.p)) continue;
      const entry: MdMembershipCellMeta = { o: raw.o, r: raw.r, p: raw.p };
      const prov = sanitiseProvenance(raw.prov);
      if (prov && Object.keys(prov).length) entry.prov = prov;
      out[propertyId] = entry;
    }
    return Object.keys(out).length ? out : undefined;
  }

  function sanitiseEdge(value: unknown, expectedId?: string): MdMembershipEdge | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as Partial<MdMembershipEdge>;
    const id = expectedId || raw.id;
    if (!validEdgeId(id) || (raw.id != null && raw.id !== id)
      || !validDbId(raw.dbId) || !validRef(raw.k)) return null;
    const edge: MdMembershipEdge = {
      id,
      dbId: raw.dbId,
      k: raw.k,
      v: raw.v && typeof raw.v === 'object' && !Array.isArray(raw.v)
        ? raw.v as Record<string, unknown> : {},
      at: Number.isFinite(raw.at) ? raw.at as number : Date.now(),
      order: Number.isInteger(raw.order) && (raw.order as number) >= 0
        ? raw.order as number : Number.MAX_SAFE_INTEGER,
      by: actorOf(raw.by),
    };
    const m = sanitiseMetaMap(raw.m);
    if (m) edge.m = m;
    return edge;
  }

  function isProjection(value: unknown): value is string[] {
    return Array.isArray(value) && value.every(validEdgeId);
  }

  function projectionIds(dbId: string): string[] {
    const raw = g.mdRead(projectionKey(dbId), []);
    return isProjection(raw) ? [...new Set(raw)].slice(0, MAX_MEMBERS) : [];
  }

  function writeProjection(dbId: string, ids: string[]): boolean {
    const clean = [...new Set(ids.filter(validEdgeId))].slice(0, MAX_MEMBERS);
    return clean.length
      ? g.mdWrite(projectionKey(dbId), clean)
      : g.mdRemove(projectionKey(dbId));
  }

  /* ── the edge set, read once per change ───────────────────────────────────
     This walks every key on the device and then reads every edge document, and
     for a long time nothing cached it while every caller asked per record. On
     a restored 5,000-record account that was the whole story of an unusable
     app — measured at boot, before the bundles, one blocked main thread:

         long task on load        25,147 ms
         mdMembershipList          4,449 calls / 25,957 ms
         localStorage.key()   56,396,080 calls
         mdRead                9,511,056 calls   (= 4,449 x 2,137 edges)

     mdBookReconcileAll reconciles every record at boot, each one asking
     bookEdge(refKey), which listed ALL edges and then .find()-ed one.

     Cached against mdStorageRevision(), the counter at the storage chokepoint
     that moves on every write and every removal — so any change to any edge,
     from a local write or a sync pull, retires this. Same signal the Librarian
     composes against; there is deliberately only one invalidation story in
     this codebase. */
  let edgeMemo: { revision: number; edges: MdMembershipEdge[]; byDbKey: Map<string, MdMembershipEdge> } | null = null;

  function edgeState(): { edges: MdMembershipEdge[]; byDbKey: Map<string, MdMembershipEdge> } {
    const revision = typeof g.mdStorageRevision === 'function' ? g.mdStorageRevision() : null;
    if (edgeMemo && revision !== null && edgeMemo.revision === revision) return edgeMemo;
    const edges: MdMembershipEdge[] = [];
    const byDbKey = new Map<string, MdMembershipEdge>();
    for (const key of g.mdStorageKeys(E_PREFIX)) {
      const id = key.slice(E_PREFIX.length);
      const edge = sanitiseEdge(g.mdRead(key, null), id);
      if (!edge) continue;
      edges.push(edge);
      /* First edge wins for a (database, record) pair, matching what the
         open-coded .find() answered when two edges named the same record. */
      const pair = `${edge.dbId}\0${edge.k}`;
      if (!byDbKey.has(pair)) byDbKey.set(pair, edge);
    }
    const built = { edges, byDbKey };
    if (revision !== null) edgeMemo = { revision, ...built };
    return built;
  }

  function allEdges(): MdMembershipEdge[] {
    return edgeState().edges;
  }

  /** Is this record a member of this database, and on what edge. A keyed
      lookup, because every caller was open-coding a linear scan of every edge
      on the device to answer it — including once per record at boot. */
  function mdMembershipFind(dbId: unknown, refKey: unknown): MdMembershipEntry | null {
    if (!validDbId(dbId) || typeof refKey !== 'string' || !refKey) return null;
    const edge = edgeState().byDbKey.get(`${String(dbId)}\0${refKey}`);
    return edge ? entryOf(edge) : null;
  }

  function edgesForDb(dbId: string): MdMembershipEdge[] {
    return allEdges().filter((edge) => edge.dbId === dbId).sort((a, b) =>
      a.order - b.order || a.at - b.at || a.id.localeCompare(b.id));
  }

  function migrateLegacy(dbId: string, value: unknown): MdMembershipEdge[] {
    const { entries } = sanitiseLegacy(value);
    const edges = entries.map((entry, order) => ({ ...entry, dbId, order }));
    for (const edge of edges) g.mdWrite(edgeKey(edge.id), edge);
    writeProjection(dbId, edges.map((edge) => edge.id));
    return edges;
  }

  function orderedEdges(dbId: string): MdMembershipEdge[] {
    const raw = g.mdRead(projectionKey(dbId), null);
    if (raw != null && !isProjection(raw)) return migrateLegacy(dbId, raw);

    const edges = edgesForDb(dbId);
    const byId = new Map(edges.map((edge) => [edge.id, edge]));
    const ids = isProjection(raw) ? [...new Set(raw)] : [];
    const ordered: MdMembershipEdge[] = [];
    const used = new Set<string>();
    for (const id of ids) {
      const edge = byId.get(id);
      if (edge) { ordered.push(edge); used.add(id); }
    }
    for (const edge of edges) if (!used.has(edge.id)) ordered.push(edge);
    if (raw == null && ordered.length) writeProjection(dbId, ordered.map((edge) => edge.id));
    return ordered;
  }

  function mdMembershipList(dbId: unknown): MdMembershipEntry[] {
    if (!validDbId(dbId)) return [];
    return orderedEdges(dbId).map(entryOf);
  }

  function mdMembershipEntry(dbId: unknown, entryId: unknown): MdMembershipEntry | null {
    if (!validDbId(dbId) || !validEdgeId(entryId)) return null;
    const edge = sanitiseEdge(g.mdRead(edgeKey(entryId), null), entryId);
    return edge && edge.dbId === dbId ? entryOf(edge) : null;
  }

  /* The full authority document for one edge — including the per-property D3
     metadata map that entryOf deliberately leaves out of the view projection.
     The organizer plans guarded mutations from this; nothing else should need
     more than the entry. Edge ids are globally unique, so no dbId is asked
     for: the dbId carried inside the document is itself the claim. */
  function mdMembershipEdgeGet(edgeId: unknown): MdMembershipEdge | null {
    if (!validEdgeId(edgeId)) return null;
    return sanitiseEdge(g.mdRead(edgeKey(edgeId), null), edgeId);
  }

  function mdMembershipAddWithId(dbId: unknown, refKey: unknown, edgeId: unknown): boolean {
    if (!validDbId(dbId) || !validRef(refKey) || !validEdgeId(edgeId)) return false;
    const edges = orderedEdges(dbId);
    if (edges.some((edge) => edge.k === refKey)) return true;
    const collision = allEdges().find((edge) => edge.id === edgeId);
    if (collision) return collision.dbId === dbId && collision.k === refKey;
    if (edges.length >= MAX_MEMBERS) return false;
    const edge: MdMembershipEdge = {
      id: edgeId, dbId, k: refKey, v: {}, at: Date.now(),
      order: edges.reduce((max, item) => Math.max(max, item.order), -1) + 1,
      by: actorNow(),
    };
    const edgeOk = g.mdWrite(edgeKey(edge.id), edge);
    const projectionOk = writeProjection(dbId, [...projectionIds(dbId), edge.id]);
    return edgeOk && projectionOk;
  }

  function mdMembershipAdd(dbId: unknown, refKey: unknown): boolean {
    return mdMembershipAddWithId(dbId, refKey, newId());
  }

  function mdMembershipRemove(dbId: unknown, refKey: unknown): boolean {
    if (!validDbId(dbId) || typeof refKey !== 'string') return false;
    const edge = orderedEdges(dbId).find((item) => item.k === refKey);
    if (!edge) return true;
    const edgeOk = g.mdRemove(edgeKey(edge.id));
    const projectionOk = writeProjection(dbId,
      projectionIds(dbId).filter((id) => id !== edge.id));
    return edgeOk && projectionOk;
  }

  function mdMembershipRemoveByRow(dbId: unknown, entryId: unknown): boolean {
    const entry = mdMembershipEntry(dbId, entryId);
    return entry ? mdMembershipRemove(dbId, entry.k) : true;
  }

  function mdMembershipCellSet(dbId: unknown, entryId: unknown, propertyId: unknown, value: unknown): boolean {
    if (!validDbId(dbId) || !validEdgeId(entryId)
      || typeof propertyId !== 'string' || !propertyId) return false;
    const edge = sanitiseEdge(g.mdRead(edgeKey(entryId), null), entryId);
    if (!edge || edge.dbId !== dbId) return false;
    /* The stamp is re-made on every write, not kept from the first: `by` answers
       "who wrote this edge", and an agent filling a cell on an edge a person
       opened is the agent's write. */
    const next = { ...edge, v: { ...edge.v }, at: Date.now(), by: actorNow() };
    if (value == null) delete next.v[propertyId];
    else next.v[propertyId] = value;
    return g.mdWrite(edgeKey(edge.id), next);
  }

  function mdMembershipReplace(dbId: unknown, value: unknown): boolean {
    if (!validDbId(dbId)) return false;
    const { entries } = sanitiseLegacy(value);
    const existing = edgesForDb(dbId);
    const keep = new Set(entries.map((entry) => entry.id));
    let ok = true;
    for (let order = 0; order < entries.length; order++) {
      const edge = { ...entries[order]!, dbId, order };
      if (!g.mdWrite(edgeKey(edge.id), edge)) ok = false;
    }
    for (const edge of existing) {
      if (!keep.has(edge.id) && !g.mdRemove(edgeKey(edge.id))) ok = false;
    }
    if (!writeProjection(dbId, entries.map((entry) => entry.id))) ok = false;
    return ok;
  }

  function mdMembershipsOf(refKey: unknown): string[] {
    if (typeof refKey !== 'string' || !refKey) return [];
    return [...new Set(allEdges().filter((edge) => edge.k === refKey)
      .map((edge) => edge.dbId))];
  }

  function mdMembershipSyncDocs(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    const dbIds = new Set<string>();
    for (const edge of allEdges()) {
      out[`edge:${edge.id}`] = edge;
      dbIds.add(edge.dbId);
    }
    for (const key of g.mdStorageKeys(P_PREFIX)) {
      const dbId = key.slice(P_PREFIX.length);
      if (validDbId(dbId)) dbIds.add(dbId);
    }
    for (const dbId of dbIds) {
      const ids = orderedEdges(dbId).map((edge) => edge.id);
      if (ids.length) out[`memberships:${dbId}`] = ids;
    }
    return out;
  }

  function mdMembershipSanitiseSyncDoc(key: unknown, value: unknown): unknown {
    if (typeof key !== 'string') return undefined;
    if (key.startsWith('edge:')) {
      const id = key.slice('edge:'.length);
      if (!validEdgeId(id)) return undefined;
      return value == null ? null : sanitiseEdge(value, id) || undefined;
    }
    if (!key.startsWith('memberships:')) return undefined;
    const dbId = key.slice('memberships:'.length);
    if (!validDbId(dbId)) return undefined;
    if (value == null) return null;
    if (isProjection(value)) return [...new Set(value)].slice(0, MAX_MEMBERS);
    if (!Array.isArray(value)) return undefined;
    return sanitiseLegacy(value).entries;
  }

  function mdMembershipApplyDoc(key: unknown, value: unknown): boolean {
    if (typeof key !== 'string') return false;
    if (key.startsWith('edge:')) {
      const id = key.slice('edge:'.length);
      if (!validEdgeId(id)) return false;
      const existing = sanitiseEdge(g.mdRead(edgeKey(id), null), id);
      if (value == null) {
        const removed = g.mdRemove(edgeKey(id));
        if (!existing) return removed;
        const projected = writeProjection(existing.dbId,
          projectionIds(existing.dbId).filter((edgeId) => edgeId !== id));
        return removed && projected;
      }
      const edge = sanitiseEdge(value, id);
      if (!edge || !g.mdWrite(edgeKey(id), edge)) return false;
      if (existing && existing.dbId !== edge.dbId) {
        writeProjection(existing.dbId,
          projectionIds(existing.dbId).filter((edgeId) => edgeId !== id));
      }
      const ids = projectionIds(edge.dbId);
      return ids.includes(id) || writeProjection(edge.dbId, [...ids, id]);
    }

    if (!key.startsWith('memberships:')) return false;
    const dbId = key.slice('memberships:'.length);
    if (!validDbId(dbId)) return false;
    // A10: the projection is never deletion authority. Edge tombstones remove
    // memberships; a missing projection is rebuilt from surviving edge docs.
    if (value == null) return g.mdRemove(projectionKey(dbId));
    if (isProjection(value)) return writeProjection(dbId, value);
    if (!Array.isArray(value)) return false;
    migrateLegacy(dbId, value);
    return true;
  }

  Object.assign(g, { mdMembershipList, mdMembershipEntry, mdMembershipFind, mdMembershipEdgeGet,
    mdMembershipAdd, mdMembershipAddWithId, mdMembershipRemove, mdMembershipRemoveByRow,
    mdMembershipCellSet, mdMembershipReplace, mdMembershipsOf,
    mdMembershipSyncDocs, mdMembershipSanitiseSyncDoc, mdMembershipApplyDoc });

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { mdMembershipList, mdMembershipEntry, mdMembershipFind, mdMembershipEdgeGet,
      mdMembershipAdd, mdMembershipAddWithId, mdMembershipRemove, mdMembershipRemoveByRow,
      mdMembershipCellSet, mdMembershipReplace, mdMembershipsOf,
      mdMembershipSyncDocs, mdMembershipSanitiseSyncDoc, mdMembershipApplyDoc };
  }
})(typeof window !== 'undefined' ? window : globalThis);
